<template>
  <div :class="{ 'program-header--cover': isCover }">
    <div class="text-center">
      <div
        class="header-logo-slot"
        :class="{
          'header-logo-slot--cover': isCover,
          'header-logo-slot--compact': !isCover && !showDetails,
          'header-logo-slot--client': isCover && usingClientLogo,
        }"
      >
        <img :src="logoSrc" class="header-logo" :class="logoClass" alt="" @error="onLogoError" />
      </div>
      <template v-if="showDetails">
        <div class="header-details" :class="{ 'header-details--cover': isCover }">
          <div class="header-client-name">{{ quote.title ? quote.title : "" }}</div>
          <div class="header-program-info">
            <span v-if="!hideReference && !isCover" class="header-reference">{{ quote.reference }} </span>
            <span>{{ tripDuration }}</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
const clientLogoCache = new Map();
const clientLogoRequests = new Map();
const SUPERNATURE_LOGO = require("@/assets/img/Logo.png");

export default {
  props: {
    quote: {
      type: Object,
    },
    showDetails: {
      type: Boolean,
      default: true,
    },
    isCover: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      clientLogoSrc: null,
      logoFailed: false,
    };
  },
  computed: {
    hideReference() {
      return this.quote?.program?.hideReference === true;
    },
    hostName() {
      return this.$store.getters["host"];
    },
    usingClientLogo() {
      return !this.logoFailed && !!this.clientLogoSrc;
    },
    logoClass() {
      if (this.isCover) return "header-logo--cover";
      if (!this.showDetails) return "header-logo--compact";
      return "";
    },
    tripDuration() {
      const days = (this.quote?.nights ?? 0) + 2;
      const nights = this.quote?.nights ?? 0;
      const start = this.quote?.cityStart ?? "";
      const end = this.quote?.cityEnd ?? "";
      return `${days} jours ${nights} nuits • ${start} – ${end}`;
    },
    logoSrc() {
      if (this.usingClientLogo) {
        return this.clientLogoSrc;
      }
      return SUPERNATURE_LOGO;
    },
  },
  watch: {
    "quote.client": {
      immediate: true,
      deep: true,
      handler() {
        this.resolveClientLogo();
      },
    },
  },
  methods: {
    onLogoError() {
      // Broken / missing client file → use Supernature logo.
      if (this.clientLogoSrc) {
        this.logoFailed = true;
        this.clientLogoSrc = null;
      }
    },
    resolveLogoUrl(logo) {
      if (!logo || typeof logo !== "object") return null;
      const raw = String(logo.src || "").trim();
      if (!raw) return null;
      if (/^https?:\/\//i.test(raw)) return raw;
      return `${this.hostName}${raw}`;
    },
    resolveClientLogo() {
      this.logoFailed = false;

      const primaryLogo = this.quote?.client?.primary_logo;
      const fromQuote = this.resolveLogoUrl(primaryLogo);
      if (fromQuote) {
        this.clientLogoSrc = fromQuote;
        return;
      }

      const clientId = this.quote?.client?.id || this.quote?.client_id;
      if (!clientId) {
        this.clientLogoSrc = null;
        return;
      }

      if (clientLogoCache.has(clientId)) {
        this.clientLogoSrc = clientLogoCache.get(clientId) || null;
        return;
      }

      // Keep Supernature visible while loading; only swap if a real logo is found.
      this.clientLogoSrc = null;

      let request = clientLogoRequests.get(clientId);
      if (!request) {
        request = this.$store
          .dispatch("ClientImageStore/getImageList", clientId)
          .then((images) => {
            const list = images || [];
            const logo = list.find((image) => image && image.isdefault) || list[0] || null;
            const url = this.resolveLogoUrl(logo);
            clientLogoCache.set(clientId, url || null);
            return url || null;
          })
          .catch(() => {
            clientLogoCache.set(clientId, null);
            return null;
          })
          .finally(() => {
            clientLogoRequests.delete(clientId);
          });
        clientLogoRequests.set(clientId, request);
      }

      request.then((url) => {
        const stillSameClient =
          String(this.quote?.client?.id || this.quote?.client_id || "") === String(clientId);
        if (!stillSameClient) return;
        this.clientLogoSrc = url || null;
      });
    },
  },
};
</script>

<style scoped>
.program-header--cover {
  width: 100%;
}

.header-logo-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.header-logo-slot--cover {
  width: min(400px, 92%);
  height: 220px;
  min-height: 220px;
  box-sizing: border-box;
}

/* Client logos are often tightly cropped; keep them vertically centered in the same slot
   as the Supernature logo so they don't sit against the top of the page. */
.header-logo-slot--cover.header-logo-slot--client {
  padding: 28px 12px;
}

.header-logo-slot--compact {
  height: 90px;
  min-height: 90px;
}

.header-logo {
  height: 120px;
  width: auto;
  max-width: 100%;
  object-fit: contain;
  object-position: center;
  display: block;
  margin: 0 auto;
}

.header-logo--cover {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  margin: 0;
  object-fit: contain;
  object-position: center;
}

.header-details--cover {
  margin-top: 72px;
}

.header-details--cover .header-client-name {
  margin-top: 0;
  font-size: 24pt;
  font-weight: 400;
  letter-spacing: 0.01em;
}

.header-details--cover .header-program-info {
  margin-top: 8px;
  font-size: 10pt;
  font-weight: 400;
}

.header-logo--compact {
  height: auto;
  max-height: 100%;
  width: auto;
}

.header-details {
  width: 100%;
}

.header-client-name {
  font-family: "Calibri", sans-serif;
  font-size: 26pt;
  color: #c5a069;
  line-height: 1.25;
  margin-top: 12px;
}

.header-program-info {
  font-family: "Calibri", sans-serif;
  font-size: 10.5pt;
  color: #c5a069;
  margin-top: 10px;
  line-height: 1.4;
}
</style>
